<template>
  <div class="login-container">
    <div class="login-card">
      <Card class="login-form">
        <template #title>
          <div class="login-header">
            <h1>TestHR</h1>
            <p>Платформа тестування персоналу</p>
          </div>
        </template>
        
        <template #content>
          <form @submit.prevent="handleLogin" class="login-form-content">
            <div class="form-group">
              <label for="username">Логін</label>
              <InputText
                id="username"
                v-model="credentials.username"
                placeholder="Введіть ваш логін"
                class="w-full"
                :class="{ 'p-invalid': errors.username }"
              />
              <small v-if="errors.username" class="p-error">{{ errors.username }}</small>
            </div>
            
            <div class="form-group">
              <label for="password">Пароль</label>
              <Password
                id="password"
                v-model="credentials.password"
                placeholder="Введіть ваш пароль"
                class="w-full"
                :class="{ 'p-invalid': errors.password }"
                :feedback="false"
                toggleMask
              />
              <small v-if="errors.password" class="p-error">{{ errors.password }}</small>
            </div>
            
            <div class="form-actions">
              <Button
                type="submit"
                label="Увійти"
                icon="pi pi-sign-in"
                class="w-full"
                :loading="loading"
                :disabled="loading"
              />
            </div>
            
            <div v-if="errorMessage" class="error-message">
              <Message severity="error" :closable="true" @close="clearError">
                {{ errorMessage }}
              </Message>
            </div>
          </form>
        </template>
      </Card>
    </div>
    
    <!-- Footer -->
    <Footer />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Footer from '../components/Footer.vue'

export default {
  name: 'Login',
  components: {
    Footer
  },
  data() {
    return {
      credentials: {
        username: '',
        password: ''
      },
      errors: {
        username: '',
        password: ''
      },
      localError: ''
    }
  },
  watch: {
    // Очищаємо помилки при зміні полів
    'credentials.username'() {
      if (this.errors.username) {
        this.errors.username = ''
      }
      if (this.localError) {
        this.localError = ''
      }
    },
    'credentials.password'() {
      if (this.errors.password) {
        this.errors.password = ''
      }
      if (this.localError) {
        this.localError = ''
      }
    }
  },
  computed: {
    ...mapState(['loading', 'error']),
    errorMessage() {
      // Перевіряємо локальну помилку
      if (this.localError && this.localError.trim()) {
        return this.localError.trim()
      }
      
      // Перевіряємо помилку з store
      if (this.error && this.error.trim()) {
        return this.error.trim()
      }
      
      // Якщо немає помилок, повертаємо null
      return null
    }
  },
  methods: {
    ...mapActions(['login']),
    
    validateForm() {
      this.errors = {
        username: '',
        password: ''
      }
      this.localError = ''
      
      let isValid = true
      
      if (!this.credentials.username.trim()) {
        this.errors.username = 'Логін обов\'язковий'
        isValid = false
      }
      
      if (!this.credentials.password) {
        this.errors.password = 'Пароль обов\'язковий'
        isValid = false
      }
      
      return isValid
    },
    
    async handleLogin() {
      if (!this.validateForm()) {
        return
      }
      
      try {
        this.localError = ''
        // Очищаємо помилки з store
        this.$store.commit('SET_ERROR', null)
        await this.login(this.credentials)
        this.$router.push('/dashboard')
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Ви успішно увійшли в систему',
          life: 3000
        })
      } catch (error) {
        const errorDetail = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Помилка входу в систему'
        
        this.localError = errorDetail
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: errorDetail,
          life: 5000
        })
      }
    },
    clearError() {
      this.localError = ''
      this.$store.commit('SET_ERROR', null)
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-600) 100%);
  position: relative;
}

.login-card {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.login-form {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border-radius: 12px;
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.login-header h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
}

.login-header p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.login-form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.9rem;
}

.form-actions {
  margin-top: 1rem;
}

.error-message {
  margin-top: 1rem;
}

/* Utility classes */
.w-full {
  width: 100%;
}

@media (max-width: 480px) {
  .login-container {
    padding: 1rem;
  }
  
  .login-card {
    max-width: 100%;
    padding: 1rem;
    margin: 0;
  }
  
  .login-header h1 {
    font-size: 1.5rem;
  }
  
  .login-header p {
    font-size: 0.9rem;
  }
}

/* Footer styling for login page */
:deep(.app-footer) {
  background: transparent;
  border-top: none;
  color: white;
  margin-top: auto;
  position: relative;
  z-index: 10;
}

:deep(.app-footer .footer-title) {
  color: white;
}

:deep(.app-footer .footer-description) {
  color: rgba(255, 255, 255, 0.8);
}

:deep(.app-footer .footer-section-title) {
  color: white;
}

:deep(.app-footer .footer-link) {
  color: rgba(255, 255, 255, 0.8);
}

:deep(.app-footer .footer-link:hover) {
  color: white;
}

:deep(.app-footer .footer-link.router-link-active) {
  color: white;
}

:deep(.app-footer .footer-copyright) {
  color: rgba(255, 255, 255, 0.7);
}

:deep(.app-footer .version-number) {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

:deep(.app-footer .build-date) {
  color: rgba(255, 255, 255, 0.7);
}

/* Additional responsive footer styles */
@media (max-width: 768px) {
  :deep(.app-footer .footer-links) {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  :deep(.app-footer .footer-links) {
    grid-template-columns: 1fr;
  }
  
  :deep(.app-footer .footer-content) {
    padding: 1.5rem 1rem;
  }
}
</style> 